import { useEffect } from 'react';
import { useRouter, type NextRouter } from 'next/router';
import type { UrlObject } from 'url';

type RouterUrl = string | UrlObject;

function toHref(url: RouterUrl): string {
  if (typeof url === 'string') return url;

  const pathname = url.pathname || '/';
  const query = url.query;
  if (!query || typeof query === 'string') {
    const search =
      typeof query === 'string' && query.length > 0
        ? query.startsWith('?')
          ? query
          : `?${query}`
        : '';
    return `${pathname}${search}${url.hash || ''}`;
  }

  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value == null) return;
    if (Array.isArray(value)) {
      value.forEach(v => {
        if (v != null) params.append(key, String(v));
      });
      return;
    }
    params.set(key, String(value));
  });

  const qs = params.toString();
  return `${pathname}${qs ? `?${qs}` : ''}${url.hash || ''}`;
}

function pathAndQuery(href: string): string {
  try {
    const parsed = new URL(href, window.location.origin);
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return href;
  }
}

/**
 * Full-page navigate to `href`. If the address bar is already there (soft nav
 * updated the URL but the page UI never swapped), reload instead of assign —
 * assign to the current URL is a no-op in most browsers.
 */
export function forceNavigate(href: string): void {
  if (typeof window === 'undefined') return;
  const target = pathAndQuery(href);
  const current = `${window.location.pathname}${window.location.search}`;
  if (current === target) {
    window.location.reload();
    return;
  }
  window.location.assign(href);
}

/**
 * Browser back/forward from a heavy detail page can update the URL while the
 * current page stays mounted. Force a full load of the history target instead.
 */
export function useHardBackNavigation(): void {
  const router = useRouter();

  useEffect(() => {
    router.beforePopState(({ as }) => {
      window.location.assign(as);
      return false;
    });
    return () => {
      router.beforePopState(() => true);
    };
  }, [router]);
}

/**
 * Soft-navigate with a hard-navigation fallback.
 * Prevents stuck UI where the address bar updates but the page component never swaps
 * (common after history.pushState desync or aborted/hung client transitions).
 */
export function safeRouterPush(
  router: NextRouter,
  url: RouterUrl,
  timeoutMs = 2500
): Promise<boolean> {
  if (typeof window === 'undefined') {
    return router.push(url);
  }

  const href = toHref(url);
  let settled = false;

  const timer = window.setTimeout(() => {
    if (settled) return;
    forceNavigate(href);
  }, timeoutMs);

  return router.push(url).finally(() => {
    settled = true;
    window.clearTimeout(timer);
  });
}
